import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { API } from '../utils';
import { toast } from 'sonner';
import axios from 'axios';
import { LogOut, Menu, X, Bell, Trash2, Shield } from 'lucide-react';
import { Button } from './ui/button';

// Custom nav icons matching brand HTML design
const IconDashboard = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <rect x="2" y="2" width="7" height="7" rx="1.5"/><rect x="11" y="2" width="7" height="7" rx="1.5"/>
    <rect x="2" y="11" width="7" height="7" rx="1.5"/><rect x="11" y="11" width="7" height="7" rx="1.5"/>
  </svg>
);
const IconBookings = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <rect x="2" y="3" width="16" height="15" rx="2"/><path d="M2 8h16M6 1v4M14 1v4"/>
  </svg>
);
const IconAvailability = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <rect x="2" y="3" width="16" height="15" rx="2"/><path d="M2 8h16M6 1v4M14 1v4"/>
    <circle cx="7" cy="13" r="1" fill="currentColor" stroke="none"/>
    <circle cx="10" cy="13" r="1" fill="currentColor" stroke="none"/>
    <circle cx="13" cy="13" r="1" fill="currentColor" stroke="none"/>
  </svg>
);
const IconMessages = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M17 4H3a1 1 0 00-1 1v9a1 1 0 001 1h3l2 3 2-3h7a1 1 0 001-1V5a1 1 0 00-1-1z"/>
    <path d="M6 9h8M6 12h4"/>
  </svg>
);
const IconProfile = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <circle cx="10" cy="7" r="4"/><path d="M2 18c0-3.5 3.6-6 8-6s8 2.5 8 6"/>
  </svg>
);
const IconFeedback = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M9 4H5a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4"/>
    <path d="M17 3l-8 8M13 3h4v4"/>
  </svg>
);
const IconHelp = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <circle cx="10" cy="10" r="8"/><path d="M10 6v4M10 14v.5"/>
  </svg>
);
const IconBrowse = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <circle cx="9" cy="9" r="6"/><path d="M15 15l3 3"/>
  </svg>
);
const IconProjects = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <rect x="2" y="3" width="16" height="14" rx="2"/>
    <path d="M6 7h8M6 10h5M6 13h3"/>
  </svg>
);

const AppLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifPos, setNotifPos] = useState({ top: 0, left: 0 });
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const bellRef = useRef(null);

  const config = { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/notifications`, config);
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 10 seconds
    const interval = setInterval(fetchNotifications, 10000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, config);
      setNotifications(notifications.map(n => 
        n.notification_id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const deleteNotification = async (e, notificationId) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/notifications/${notificationId}`, config);
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
      setUnreadCount(prev => {
        const wasUnread = notifications.find(n => n.notification_id === notificationId)?.is_read === false;
        return wasUnread ? Math.max(0, prev - 1) : prev;
      });
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  const markAllAsRead = async (e) => {
    e.stopPropagation();
    try {
      await axios.put(`${API}/notifications/read-all`, {}, config);
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark notifications as read');
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.notification_id);
    }
    {
      let targetLink = notification.link || null;
      if (notification.type === 'booking_request') {
        targetLink = '/bookings?tab=requests';
      } else if (notification.type === 'counter_proposal') {
        targetLink = notification.metadata?.booking_id
          ? `/bookings?tab=requests&highlight=${notification.metadata.booking_id}`
          : '/bookings?tab=requests';
      } else if (notification.type === 'booking_accepted') {
        targetLink = '/bookings?tab=active';
      } else if (notification.type === 'agreement_fully_signed' || notification.type === 'agreement_signed') {
        targetLink = '/bookings';
      } else if (targetLink?.match(/^\/bookings\/booking_[a-z0-9]+$/)) {
        targetLink = '/bookings';
      }
      if (targetLink) navigate(targetLink);
    }
    setNotificationsOpen(false);
  };

  const getNotificationIcon = (type) => {
    const cls = "w-4 h-4";
    switch (type) {
      case 'booking_request':
      case 'booking_accepted':
        return <svg className={cls} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="14" height="14" rx="2"/><path d="M3 8h14M7 2v3M13 2v3"/></svg>;
      case 'new_application':
      case 'application_accepted':
        return <svg className={cls} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 3h8l3 3v11a1 1 0 01-1 1H5a1 1 0 01-1-1V4a1 1 0 011-1z"/><path d="M13 3v4h3"/><path d="M7 9h6M7 12h4"/></svg>;
      case 'application_rejected':
      case 'application_withdrawn':
        return <svg className={cls} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="10" cy="10" r="7"/><path d="M7 7l6 6M13 7l-6 6"/></svg>;
      case 'booking_withdrawn':
        return <svg className={cls} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="10" cy="10" r="7"/><path d="M7 10h6"/></svg>;
      case 'deliverables_approved':
        return <svg className={cls} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="10" cy="10" r="7"/><path d="M7 10l2 2 4-4"/></svg>;
      case 'deliverables_uploaded':
        return <svg className={cls} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13V4M6 7l4-4 4 4"/><path d="M3 15v1a2 2 0 002 2h10a2 2 0 002-2v-1"/></svg>;
      case 'new_review':
        return <svg className={cls} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10 2l2.4 4.9 5.4.8-3.9 3.8.9 5.3L10 14.4l-4.8 2.5.9-5.3L2.2 7.7l5.4-.8L10 2z"/></svg>;
      case 'payment_received':
        return <svg className={cls} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="16" height="12" rx="2"/><path d="M2 9h16M6 13h2"/></svg>;
      case 'new_message':
        return <svg className={cls} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5a2 2 0 012-2h10a2 2 0 012 2v7a2 2 0 01-2 2H7l-4 3V5z"/></svg>;
      case 'agreement_ready':
      case 'agreement_signed':
      case 'agreement_fully_signed':
        return <svg className={cls} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 3h8l3 3v11a1 1 0 01-1 1H5a1 1 0 01-1-1V4a1 1 0 011-1z"/><path d="M13 3v4h3"/><path d="M7 12l2 2 4-4"/></svg>;
      case 'revision_requested':
        return <svg className={cls} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 10a6 6 0 1012 0 6 6 0 00-12 0"/><path d="M4 6V3M4 6H1" strokeLinecap="round"/></svg>;
      case 'welcome_creative':
      case 'welcome_business':
        return <svg className={cls} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10 2l1.5 3 3.5.5-2.5 2.5.5 3.5L10 10l-3 1.5.5-3.5L5 5.5l3.5-.5L10 2z"/><path d="M3 17c1-2 4-3 7-3s6 1 7 3"/></svg>;
      default:
        return <svg className={cls} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10 2a6 6 0 016 6c0 3 1 4 2 5H2c1-1 2-2 2-5a6 6 0 016-6z"/><path d="M8 17a2 2 0 004 0"/></svg>;
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const menuItems = [
    { icon: IconDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: IconBrowse, label: 'Browse Photographers', path: '/photographers', show: user?.role === 'business' },
    { icon: IconBookings, label: 'Bookings', path: '/bookings' },
    { icon: IconProjects, label: 'Browse Projects', path: '/projects', show: user?.role === 'creative' },
    { icon: IconProjects, label: 'My Projects', path: '/projects', show: user?.role === 'business' },
    { icon: IconAvailability, label: 'Availability', path: '/availability', show: user?.role === 'creative' },
    { icon: IconMessages, label: 'Messages', path: '/messages' },
    { icon: IconProfile, label: 'Edit Profile', path: '/edit-profile', show: user?.role === 'creative' },
    { icon: IconProfile, label: 'Edit Profile', path: '/edit-business-profile', show: user?.role === 'business' },
    { icon: IconFeedback, label: 'Give Feedback', path: '/feedback' },
    { icon: IconHelp, label: 'Help Center', path: '/help' },
    { icon: Shield, label: 'Admin Panel', path: '/admin', show: user?.is_admin === true },
  ].filter(item => item.show !== false);

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <div className="h-screen bg-background flex overflow-x-hidden">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col w-[220px] bg-[#f7f5f2] border-r border-[#e4e1db] px-4 py-6 h-screen sticky top-0">
        <Link to="/dashboard" className="font-display text-[20px] text-ink pb-5 mb-2 border-b border-[#e4e1db] block">
          FrameFinder
        </Link>

        <nav className="flex-1 flex flex-col gap-[3px] mt-2">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-2.5 px-3 py-[9px] rounded-[8px] transition-all ${
                isActive(item.path)
                  ? 'bg-ink text-white'
                  : 'text-[#5a5a5a] hover:bg-[#e4e1db] hover:text-ink'
              }`}
            >
              <item.icon className="w-[15px] h-[15px] flex-shrink-0" />
              <span className="text-[13px] font-medium">{item.label}</span>
            </Link>
          ))}

          {/* Notification Bell — in nav, below Help Center */}
          <div className="relative">
            <button
              ref={bellRef}
              onClick={() => {
                if (!notificationsOpen && bellRef.current) {
                  const r = bellRef.current.getBoundingClientRect();
                  setNotifPos({ top: r.top, left: r.right + 8 });
                }
                setNotificationsOpen(!notificationsOpen);
              }}
              className="flex items-center gap-2.5 px-3 py-[9px] w-full text-[#5a5a5a] hover:bg-[#e4e1db] hover:text-ink rounded-[8px] transition-all"
              data-testid="notification-bell"
            >
              <div className="relative flex-shrink-0">
                <Bell className="w-[15px] h-[15px]" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-[#f87171] text-white text-[9px] font-bold rounded-full flex items-center justify-center border-2 border-[#f7f5f2]">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </div>
              <span className="text-[13px] font-medium">Notifications</span>
            </button>

            {/* Notifications Dropdown — rendered via portal so it escapes sidebar stacking context */}
            {notificationsOpen && createPortal(
              <>
                <div
                  className="fixed inset-0 z-[9998]"
                  onClick={() => setNotificationsOpen(false)}
                />
                <div
                  className="fixed bg-white border border-[#e4e1db] rounded-[14px] shadow-xl overflow-hidden z-[9999] w-80"
                  style={{ top: notifPos.top, left: notifPos.left }}
                >
                  <div className="flex items-center justify-between p-3 border-b border-[#e4e1db]">
                    <h3 className="font-semibold text-sm text-ink">Notifications</h3>
                    {unreadCount > 0 && (
                      <button
                        onClick={markAllAsRead}
                        className="text-xs text-[#d4a843] hover:text-[#8a6a1a]"
                        data-testid="mark-all-read-btn"
                      >
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notifications.length > 0 ? (
                      notifications.slice(0, 20).map((notification) => (
                        <div
                          key={notification.notification_id}
                          className={`flex items-start gap-3 p-3 border-b border-[#e4e1db] transition-colors ${
                            !notification.is_read ? 'bg-[#f5ead0]' : 'hover:bg-[#f7f5f2]'
                          }`}
                          data-testid={`notification-item-${notification.notification_id}`}
                        >
                          <button
                            onClick={() => handleNotificationClick(notification)}
                            className="flex flex-1 gap-3 text-left min-w-0"
                          >
                            <span className="shrink-0 text-[#0e0e0e] mt-0.5">{getNotificationIcon(notification.type)}</span>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${!notification.is_read ? 'font-medium' : ''}`}>
                                {notification.title}
                              </p>
                              <p className="text-xs text-[#5a5a5a] whitespace-normal">{notification.message}</p>
                              <p className="text-xs text-[#a0a0a0] mt-1">{formatTimeAgo(notification.created_at)}</p>
                            </div>
                          </button>
                          <div className="flex items-center gap-1 shrink-0">
                            {!notification.is_read && (
                              <div className="w-2 h-2 bg-[#d4a843] rounded-full" />
                            )}
                            <button
                              onClick={(e) => deleteNotification(e, notification.notification_id)}
                              className="p-1 text-[#a0a0a0] hover:text-red-500 transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-6 text-center text-[#a0a0a0] text-sm">
                        No notifications yet
                      </div>
                    )}
                  </div>
                </div>
              </>,
              document.body
            )}
          </div>
        </nav>

        {/* User Section */}
        <div className="mt-auto pt-5 border-t border-[#e4e1db] flex flex-col gap-[3px]">
          {/* User Profile */}
          <div className="flex items-center gap-2.5 px-3 py-2 mt-1">
            <div className="w-8 h-8 rounded-full bg-ink flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-semibold text-white">
                {user?.name?.charAt(0) || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold text-ink truncate">{user?.name}</p>
              <p className="text-[11px] text-[#a0a0a0]">{user?.role === 'creative' ? 'Photographer' : user?.role === 'business' ? 'Business' : user?.role}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2.5 px-3 py-[9px] w-full text-[#5a5a5a] hover:bg-[#e4e1db] hover:text-ink rounded-[8px] transition-all"
          >
            <LogOut className="w-[15px] h-[15px] flex-shrink-0" />
            <span className="text-[13px] font-medium">Log out</span>
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-[#f7f5f2] border-b border-[#e4e1db] z-50 px-4 py-3 flex items-center justify-between">
        <Link to="/dashboard" className="font-display text-[20px] text-ink">
          FrameFinder
        </Link>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setNotificationsOpen(!notificationsOpen)}
            className="relative p-2"
            data-testid="mobile-notification-bell"
          >
            <Bell className="w-[15px] h-[15px] text-[#5a5a5a]" />
            {unreadCount > 0 && (
              <span className="absolute top-0 right-0 w-4 h-4 bg-[#f87171] text-white text-[9px] font-bold rounded-full flex items-center justify-center border-2 border-[#f7f5f2]">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile Notifications Dropdown */}
      {notificationsOpen && (
        <div className="md:hidden fixed top-14 left-0 right-0 bg-white border-b border-[#e4e1db] shadow-lg max-h-72 overflow-hidden z-40">
          <div className="flex items-center justify-between p-3 border-b border-[#e4e1db]">
            <h3 className="font-semibold text-sm text-ink">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-[#d4a843] hover:text-[#8a6a1a]"
              >
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-60 overflow-y-auto">
            {notifications.length > 0 ? (
              notifications.slice(0, 20).map((notification) => (
                <div
                  key={notification.notification_id}
                  className={`flex items-start gap-3 p-3 border-b border-[#e4e1db] ${
                    !notification.is_read ? 'bg-[#f5ead0]' : ''
                  }`}
                >
                  <button
                    onClick={() => handleNotificationClick(notification)}
                    className="flex flex-1 gap-3 text-left min-w-0"
                  >
                    <span className="text-lg shrink-0">{getNotificationIcon(notification.type)}</span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!notification.is_read ? 'font-medium' : ''}`}>
                        {notification.title}
                      </p>
                      <p className="text-xs text-[#5a5a5a] whitespace-normal">{notification.message}</p>
                      <p className="text-xs text-[#a0a0a0] mt-1">{formatTimeAgo(notification.created_at)}</p>
                    </div>
                  </button>
                  <div className="flex items-center gap-1 shrink-0">
                    {!notification.is_read && (
                      <div className="w-2 h-2 bg-[#d4a843] rounded-full" />
                    )}
                    <button
                      onClick={(e) => deleteNotification(e, notification.notification_id)}
                      className="p-1 text-[#a0a0a0] hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-6 text-center text-[#a0a0a0] text-sm">
                No notifications yet
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/50" onClick={closeMobileMenu} />
          <aside className="absolute left-0 top-0 bottom-0 w-[220px] bg-[#f7f5f2] border-r border-[#e4e1db] flex flex-col px-4 py-6">
            <Link to="/dashboard" className="font-display text-[20px] text-ink pb-5 mb-2 border-b border-[#e4e1db] block" onClick={closeMobileMenu}>
              FrameFinder
            </Link>

            <nav className="flex-1 flex flex-col gap-[3px] mt-2">
              {menuItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={closeMobileMenu}
                  className={`flex items-center gap-2.5 px-3 py-[9px] rounded-[8px] transition-all ${
                    isActive(item.path)
                      ? 'bg-ink text-white'
                      : 'text-[#5a5a5a] hover:bg-[#e4e1db] hover:text-ink'
                  }`}
                >
                  <item.icon className="w-[15px] h-[15px] flex-shrink-0" />
                  <span className="text-[13px] font-medium">{item.label}</span>
                </Link>
              ))}
              <button
                onClick={() => setNotificationsOpen(!notificationsOpen)}
                className="flex items-center gap-2.5 px-3 py-[9px] w-full text-[#5a5a5a] hover:bg-[#e4e1db] hover:text-ink rounded-[8px] transition-all"
              >
                <div className="relative flex-shrink-0">
                  <Bell className="w-[15px] h-[15px]" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1.5 left-3 w-4 h-4 bg-[#f87171] text-white text-[9px] font-bold rounded-full flex items-center justify-center border-2 border-[#f7f5f2]">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </div>
                <span className="text-[13px] font-medium">Notifications</span>
              </button>
            </nav>

            <div className="mt-auto pt-5 border-t border-[#e4e1db] flex flex-col gap-[3px]">
              <div className="flex items-center gap-2.5 px-3 py-2">
                <div className="w-8 h-8 rounded-full bg-ink flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-semibold text-white">
                    {user?.name?.charAt(0) || 'U'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold text-ink truncate">{user?.name}</p>
                  <p className="text-[11px] text-[#a0a0a0]">{user?.role === 'creative' ? 'Photographer' : user?.role === 'business' ? 'Business' : user?.role}</p>
                </div>
              </div>
              <button
                onClick={() => { closeMobileMenu(); handleLogout(); }}
                className="flex items-center gap-2.5 px-3 py-[9px] w-full text-[#5a5a5a] hover:bg-[#e4e1db] hover:text-ink rounded-[8px] transition-all"
              >
                <LogOut className="w-[15px] h-[15px] flex-shrink-0" />
                <span className="text-[13px] font-medium">Log out</span>
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 md:pt-0 pt-14 overflow-x-hidden overflow-y-auto min-w-0">
        {children}
      </main>
    </div>
  );
};

export default AppLayout;
